/**
 * Helpers para Razão de Verossimilhança (LR) ENFSI.
 * Convenção forense: H1 = mesmo autor (LR > 1); H2 = autores diferentes (LR < 1);
 * LR = 1 = inconclusivo.
 *
 * Origem dos valores e fórmulas: protótipo data.jsx + // ux-neutrality:allow doc-comment
 * docs/domain/forensic-methodology.md §5. // ux-neutrality:allow doc-comment
 */

export type LRPolarity = 'h1' | 'h2' | 'inconclusive' | 'unknown';

export interface LRVerbal {
  /** Texto legível com a hipótese e força ("apoio moderado à hipótese de autoria"). */
  text: string;
  /** Polaridade da escala ENFSI. */
  polarity: LRPolarity;
  /** Magnitude normalizada [0..1] da força (|log10(LR)|/5). */
  strength: number;
}

// Banda de não-suporte (inconclusivo) — ADR-0015. Espelha a constante de
// domínio do backend (`_LR_INCONCLUSIVE_BAND_*` em types/forensic.py). LR
// dentro de [1/3, 3] não sustenta hipótese: inconclusivo, não conclusão fraca.
// E16.F6/ADR-0020: alinha o LRPill (única superfície que ainda classificava o
// LR no front) à mesma régua do LRMeter/backend e do laudo — antes só LR=1 era
// inconclusivo, o que contradizia o medidor ao lado no cabeçalho do workspace.
const LR_INCONCLUSIVE_BAND_LOW = 1 / 3;
const LR_INCONCLUSIVE_BAND_HIGH = 3;

/** Log10 do LR; null se LR não-positivo. */
export function lrLog10(lr: number | null | undefined): number | null {
  if (lr == null || lr <= 0) return null;
  return Math.log10(lr);
}

/**
 * Classificação verbal ENFSI em PT-BR (banda canônica ADR-0015 / E16.F6).
 * Banda [1/3, 3] → inconclusivo. Fora dela, força coarse igual à do backend
 * (`lr_strength_bucket`): |log10| ≥ 2 forte; ≥ 1 moderado; senão limitado. // ux-neutrality:allow doc-comment
 */
export function lrVerbal(lr: number | null | undefined): LRVerbal {
  if (lr == null) {
    return { text: '—', polarity: 'unknown', strength: 0 };
  }
  if (lr >= LR_INCONCLUSIVE_BAND_LOW && lr <= LR_INCONCLUSIVE_BAND_HIGH) {
    return { text: 'inconclusivo', polarity: 'inconclusive', strength: 0 };
  }
  const log = Math.log10(lr);
  const absLog = Math.abs(log);
  const polarity: LRPolarity = lr > 1 ? 'h1' : 'h2';
  const dir = polarity === 'h1' ? 'autoria' : 'heteroautoria';
  let strengthWord: string;
  if (absLog >= 2) strengthWord = 'forte';
  else if (absLog >= 1) strengthWord = 'moderado';
  else strengthWord = 'limitado';

  return {
    text: `apoio ${strengthWord} à hipótese de ${dir}`,
    polarity,
    strength: Math.min(absLog / 5, 1),
  };
}

/** Posição em [0..1] na escala ENFSI [-5..+5] de log10(LR). 0.5 = LR=1. */
export function lrToScalePos(lr: number | null | undefined): number {
  if (lr == null) return 0.5;
  const log = Math.max(-5, Math.min(5, Math.log10(lr)));
  return (log + 5) / 10;
}

// E39.F1/ADR-0054: índice de suporte ASSINADO −100..+100 (régua de BANCADA do
// perito). Espelha `support_index`/`support_index_level` do backend: sinal =
// direção, módulo = |log10(LR)|/2 saturado em 100 (teto LR=100), banda [1/3, 3]
// → 0. Camada de exibição/ajuste — NÃO é probabilidade e NÃO sai no laudo.
export const SUPPORT_INDEX_MIN = -100;
export const SUPPORT_INDEX_MAX = 100;
const SUPPORT_INDEX_CEILING_LOG10 = 2; // |log10(100)| — teto da régua.

/**
 * Mapeia o LR para o índice de bancada assinado −100..+100 (espelho fiel do
 * backend `support_index`): banda [1/3, 3] → 0, saturação em ±100. `null`
 * quando o LR é ausente/não-positivo. Usado para o valor de REPOUSO do controle
 * de ajuste; o número PRINCIPAL exibido vem resolvido do backend (single-source).
 */
export function lrToSupportIndex(lr: number | null | undefined): number | null {
  if (lr == null || lr <= 0) return null;
  if (lr >= LR_INCONCLUSIVE_BAND_LOW && lr <= LR_INCONCLUSIVE_BAND_HIGH) return 0;
  const magnitude = Math.min(Math.abs(Math.log10(lr)) / SUPPORT_INDEX_CEILING_LOG10, 1) * 100;
  return lr > 1 ? magnitude : -magnitude;
}

/**
 * Inverso BIJETIVO do índice para LR (`lr = 10^(índice/50)`): +100→LR=100,
 * +50→LR=10, 0→LR=1, −100→LR=1/100. O ajuste manual de bancada (ADR-0044) usa
 * isto: o perito digita o índice e a trilha persiste o LR resultante.
 */
export function supportIndexToLr(index: number): number {
  return 10 ** (index / 50);
}

/** Formata o índice assinado para leitura de bancada (`+45`, `0`, `−100`). */
export function fmtSupportIndex(index: number | null | undefined): string {
  if (index == null) return '—';
  const rounded = Math.round(index);
  if (rounded > 0) return `+${rounded}`;
  if (rounded < 0) return `−${Math.abs(rounded)}`;
  return '0';
}

/** Formata LR para exibição compacta (notação 1/N quando < 1; científica em magnitudes extremas). */
export function fmtLR(lr: number | null | undefined): string {
  if (lr == null) return '—';
  if (lr >= 1) {
    if (lr >= 10_000) {
      const [base, expPart] = lr.toExponential(1).split('e');
      const exp = Number(expPart);
      return `${base} × 10^${exp}`;
    }
    return lr.toFixed(lr < 10 ? 2 : 0);
  }
  const inv = 1 / lr;
  if (inv >= 1000) return `1 / ${inv.toFixed(0)}`;
  return lr.toFixed(lr < 0.01 ? 4 : 3);
}
